/**
 * The action plan and lender-meeting prep: the tangible thing an owner takes
 * away. Built from the workspace alone, pure, and rendered both on screen and
 * as Markdown.
 *
 * Program pathways are things to ASK A LENDER ABOUT, filtered only by what the
 * money is for and the published maximum amount. Nothing here states that the
 * owner is eligible for anything.
 */

import programsJson from '../data/programs.json';
import { describeSource, operatingIncome } from '../core/financials.js';
import { estimate, defaultRatePct, formatPer100, MODEL, PRIME } from '../core/estimate.js';
import { coverage } from '../core/financials.js';
import { findConflicts } from '../documents/reconcile.js';
import { GOAL_OPTIONS, INDUSTRY_OPTIONS, STAGE_OPTIONS, AGE_OPTIONS } from '../intake/graph.js';
import { fmtMoney } from '../intake/parseAnswer.js';
import { buildChecklist, STATUS_LABELS, type ChecklistItem } from '../readiness/rules.js';
import type { Goal, Workspace } from '../workspace/schema.js';
import { FINANCIAL_FIELD_LABELS, type FinancialField } from '../workspace/schema.js';
import type { UseOfFunds } from '../core/types.js';

interface Program {
  id: string; name: string; maxAmount: number | null; citation: string;
  summary: string; uses: string[]; usesText: string; notes: string[];
}
const PROGRAMS = programsJson.programs as Program[];
export const LENDER_MATCH = programsJson.lenderMatch;

const GOAL_USE: Record<Goal, UseOfFunds | null> = {
  'working-capital': 'working-capital',
  'equipment-expansion': 'equipment',
  'buy-business': 'business-acquisition',
  'real-estate': 'real-estate',
  refinance: 'debt-refinancing',
  'not-sure': null,
};

const label = (opts: { value: string; label: string }[], v?: string) => opts.find((o) => o.value === v)?.label;

export interface PlanFigure { label: string; value: string; source: string; confirmed: boolean }

export interface ActionPlan {
  generatedAt: string;
  request: Array<[string, string]>;
  business: Array<[string, string]>;
  figures: PlanFigure[];
  conflicts: string[];
  estimate: string[];
  coverage: string[];
  pathways: Array<{ name: string; why: string; maxText: string; citation: string; notes: string[] }>;
  missing: ChecklistItem[];
  ready: ChecklistItem[];
  documentsToBring: string[];
  questions: Array<{ topic: string; items: string[] }>;
  nextSteps: string[];
}

export function buildPlan(ws: Workspace): ActionPlan {
  const p = ws.profile;
  const goal = p.goal?.value;
  const checklist = buildChecklist(ws);

  const request: Array<[string, string]> = [];
  if (goal) request.push(['Goal', label(GOAL_OPTIONS, goal) ?? goal]);
  if (p.amount) request.push(['Amount', fmtMoney(p.amount.value)]);
  if (p.fundsDescription?.value) request.push(['Use of funds', p.fundsDescription.value]);
  if (p.purchasePrice) request.push([goal === 'real-estate' ? 'Property price' : 'Purchase price', fmtMoney(p.purchasePrice.value)]);
  if (p.ownerInjection) request.push(['Your own money going in', fmtMoney(p.ownerInjection.value)]);
  if (p.termMonths) request.push(['Repayment length considered', `${p.termMonths.value / 12} years`]);
  if (p.refinanceMonthlyPayment) request.push(['Current payment on debt to refinance', `${fmtMoney(p.refinanceMonthlyPayment.value)} a month`]);

  const business: Array<[string, string]> = [];
  if (p.industry) business.push(['Industry', label(INDUSTRY_OPTIONS, p.industry.value) ?? p.industry.value]);
  if (p.stage) business.push(['Stage', label(STAGE_OPTIONS, p.stage.value) ?? p.stage.value]);
  if (p.ageBand && p.ageBand.value !== 'not-operating') business.push(['Operating for', label(AGE_OPTIONS, p.ageBand.value) ?? p.ageBand.value]);
  if (p.jobs) business.push(['Jobs supported', String(p.jobs.value)]);

  const figures: PlanFigure[] = [];
  for (const f of Object.keys(FINANCIAL_FIELD_LABELS) as FinancialField[]) {
    const fa = p[f];
    if (!fa) continue;
    figures.push({ label: FINANCIAL_FIELD_LABELS[f], value: fmtMoney(fa.value), source: describeSource(fa), confirmed: fa.source === 'confirmed' });
  }
  const oi = operatingIncome(ws);
  if (oi?.derived && !p.operatingIncome) {
    figures.push({ label: 'Operating income (calculated)', value: fmtMoney(oi.value), source: oi.derived, confirmed: oi.confirmed });
  }

  const conflicts = findConflicts(ws).filter((c) => !c.resolved).map((c) =>
    `${c.label}: ${c.figures.map((f) => `${fmtMoney(f.value)} in ${f.docName}${f.page !== undefined ? ` p.${f.page}` : ''}`).join(' vs ')}`);

  const est = estimate(ws);
  const estimateLines: string[] = [];
  if (est.ready) {
    const r = est.result;
    estimateLines.push(`${r.bandLabel}: about ${formatPer100(r.estimatedChargeOffRate)} in 100 comparable SBA loans approved in FY${MODEL.meta.outcome.calibratedOnFiscalYears.join('-')} were charged off within ${MODEL.meta.outcome.horizonMonths / 12} years, against about ${formatPer100(MODEL.global.rate)} in 100 overall.`);
    estimateLines.push(`Rates on comparable recent variable-rate SBA loans, at today's prime (${PRIME.pct}% as of ${PRIME.asOf}): ${r.rate.estimateLowPct.toFixed(2)}%-${r.rate.estimateHighPct.toFixed(2)}%, middle ${r.rate.estimateLikelyPct.toFixed(2)}%. SBA maximum for this size: ${r.rate.ceilingPct.toFixed(2)}%.`);
    estimateLines.push(`Estimated monthly payment: ${fmtMoney(r.payment.lowMonthly)}-${fmtMoney(r.payment.highMonthly)}.`);
    estimateLines.push('This is a summary of how comparable approved loans performed, not a credit decision or a rate quote.');
  } else {
    estimateLines.push(`Not yet available — needs ${est.missing.join(', ')}.`);
  }

  const covLines: string[] = [];
  if (p.amount && p.stage?.value !== 'planning') {
    const cov = coverage(ws, p.amount.value, p.termMonths?.value ?? 120, defaultRatePct(ws));
    if (cov.ready) {
      covLines.push(`${cov.verdictLabel}: income covers total loan payments ${cov.ratio}x on confirmed figures.`);
      covLines.push(...cov.assumptions.map((a) => `Assumption: ${a}`));
    } else {
      covLines.push(`Not calculated yet — needs ${cov.needs.join(' and ')}.`);
    }
  }

  const use = goal ? GOAL_USE[goal] : null;
  const amount = p.amount?.value ?? 0;
  const pathways = PROGRAMS
    .filter((pr) => (use ? pr.uses.includes(use) : true))
    .filter((pr) => pr.maxAmount === null || amount === 0 || amount <= pr.maxAmount)
    .map((pr) => ({
      name: pr.name,
      why: `${pr.summary} Typical uses: ${pr.usesText}`,
      maxText: pr.maxAmount === null ? 'Limit not confirmed from an sba.gov source — ask a lender.' : `Published maximum: ${fmtMoney(pr.maxAmount)}.`,
      citation: pr.citation,
      notes: pr.notes,
    }));

  const missing = checklist.filter((i) => i.status !== 'ready');
  const ready = checklist.filter((i) => i.status === 'ready');

  const documentsToBring = checklist
    .filter((i) => i.group === 'documents' || i.id === 'doc-pfs')
    .map((i) => `${i.title}${i.status === 'ready' ? '' : ` — ${STATUS_LABELS[i.status].toLowerCase()}`}`);

  const questions = [
    { topic: 'Rates', items: [
      'Would the rate be fixed or variable? If variable, what is it based on, and what margin would you add?',
      'How often can the rate change, and is there a cap?',
    ] },
    { topic: 'Fees', items: [
      'What fees apply — the SBA guaranty fee, packaging, closing or servicing fees? Can any be financed?',
      'Can you give me all fees in writing before I commit?',
    ] },
    { topic: 'Repayment', items: [
      'What term would you offer for this use of funds, and why?',
      'Is there a penalty for paying the loan off early?',
    ] },
    { topic: 'Collateral and guarantees', items: [
      'What collateral would you require? Would you take a lien on my home?',
      'Who would need to sign a personal guarantee?',
    ] },
    { topic: 'Your requirements', items: [
      'What documents do you need from me, and for how many years?',
      'What debt-service coverage do you look for, and how do you calculate it?',
      goal === 'buy-business' || goal === 'real-estate' || p.stage?.value === 'planning'
        ? 'How much of my own money would you expect me to put in?'
        : 'How would you view my current debt alongside this loan?',
      'How long does approval usually take, and what could slow it down?',
    ] },
  ];

  const nextSteps: string[] = [];
  const firstMissing = missing.filter((i) => i.importance === 'likely').slice(0, 3);
  for (const i of firstMissing) nextSteps.push(`${i.title}: ${i.missing || STATUS_LABELS[i.status]}`);
  if (conflicts.length) nextSteps.push('Resolve the figures that differ between documents, or be ready to explain why.');
  nextSteps.push(`When you are ready, use ${LENDER_MATCH.name} (${LENDER_MATCH.url}) to find SBA lenders, and take this plan to your first conversation.`);

  return {
    generatedAt: new Date().toISOString(),
    request, business, figures, conflicts,
    estimate: estimateLines, coverage: covLines, pathways, missing, ready, documentsToBring, questions, nextSteps,
  };
}

export function planToMarkdown(plan: ActionPlan): string {
  const L: string[] = [];
  const date = new Date(plan.generatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  L.push('# Financing action plan', '', `Prepared ${date} with Tyche Business · Financing Assistant.`, '');
  L.push('> Not a loan offer, credit decision or rate quote. Tyche Business is an independent project, not a lender, and not affiliated with the SBA.', '');
  const table = (rows: Array<[string, string]>) => rows.forEach(([k, v]) => L.push(`- **${k}:** ${v}`));
  if (plan.request.length) { L.push('## Financing request', ''); table(plan.request); L.push(''); }
  if (plan.business.length) { L.push('## Business profile', ''); table(plan.business); L.push(''); }
  if (plan.figures.length) {
    L.push('## Known financials', '', '| Figure | Amount | Source |', '|---|---|---|');
    for (const f of plan.figures) L.push(`| ${f.label} | ${f.value} | ${f.source}${f.confirmed ? '' : ' — unconfirmed'} |`);
    L.push('');
  }
  if (plan.conflicts.length) { L.push('## Figures that differ between documents', ''); plan.conflicts.forEach((c) => L.push(`- ${c}`)); L.push(''); }
  L.push('## Estimate', ''); plan.estimate.forEach((e) => L.push(`- ${e}`)); L.push('');
  if (plan.coverage.length) { L.push('## Cash-flow coverage', ''); plan.coverage.forEach((c) => L.push(`- ${c}`)); L.push(''); }
  if (plan.pathways.length) {
    L.push('## Programs to ask a lender about', '');
    for (const pw of plan.pathways) L.push(`- **${pw.name}.** ${pw.why} ${pw.maxText} ${pw.notes.join(' ')} (${pw.citation})`);
    L.push('');
  }
  if (plan.missing.length) { L.push('## Still to do', ''); plan.missing.forEach((m) => L.push(`- **${m.title}** (${STATUS_LABELS[m.status]}): ${m.missing}`)); L.push(''); }
  L.push('## Documents to bring', ''); plan.documentsToBring.forEach((d) => L.push(`- ${d}`)); L.push('');
  L.push('## Questions to ask a lender', '');
  for (const q of plan.questions) { L.push(`**${q.topic}**`, ''); q.items.forEach((i) => L.push(`- ${i}`)); L.push(''); }
  L.push('## Next steps', ''); plan.nextSteps.forEach((s, i) => L.push(`${i + 1}. ${s}`));
  return `${L.join('\n')}\n`;
}
